import { Session } from "@/app/auth/types";
import {
  Entity,
  EntityValue,
  EntityValueFlat,
  EntityValueFlatType,
  EntityValueType,
  IField,
  ISelectFieldOption,
} from "@/app/fields/types";
import { MediaRate } from "@/app/media-rates/types";
import { Sponsorship } from "@/app/sponsorships/types";
import { User } from "@/app/users/types";

export interface ApiError {
  is_error: true;
  status: number;
  detail: string;
}

export type ApiPromise<T> = Promise<T | ApiError>;

export type ListApiCall<T extends Entity> = (
  params: URLSearchParams
) => ApiPromise<PaginatedResponse<T>>;

export type ListFieldsApiCall<T extends Entity> = () => ApiPromise<
  PaginatedResponse<IField<T>>
>;

export type ListFieldsOptionsApiCall = (
  field: string
) => ApiPromise<PaginatedResponse<ISelectFieldOption>>;

export type GetApiCall<T extends Entity> = (id: string) => ApiPromise<T>;

export type PostApiCall<T extends Entity> = (
  payload: Omit<PostPayload<T>, "id">
) => ApiPromise<T>;

export type CreateOptionApiCall = (
  field: string,
  payload: ISelectFieldOption
) => ApiPromise<ISelectFieldOption>;

export type PutApiCall<T extends Entity> = (
  payload: PostPayload<T>
) => ApiPromise<T>;

export type DeleteApiCall = (id: string) => ApiPromise<void>;

export interface ApiClient {
  getToken: (args: { email: string; password: string }) => ApiPromise<Session>;
  logout: () => ApiPromise<void>;
  requestPasswordResetTokenEmail: (args: { email: string }) => ApiPromise<void>;
  resetPassword: (args: {
    password: string;
    token: string;
  }) => ApiPromise<void>;
  signUp: (args: {
    email: string;
    password: string;
    password2: string;
    first_name: string;
    last_name: string;
  }) => ApiPromise<User>;
  getSponsorships: ListApiCall<Sponsorship>;
  getSponsorshipFieldOptions: ListFieldsOptionsApiCall;
  getSponsorship: GetApiCall<Sponsorship>;
  deleteSponsorship: DeleteApiCall;
  createSponsorship: PostApiCall<Sponsorship>;
  updateSponsorship: PutApiCall<Sponsorship>;
  getMediaRate: GetApiCall<MediaRate>;
  deleteMediaRate: DeleteApiCall;
  createMediaRate: PostApiCall<MediaRate>;
  updateMediaRate: PutApiCall<MediaRate>;
  getSponsorshipFields: ListFieldsApiCall<Sponsorship>;
  getMediaRatesFields: ListFieldsApiCall<MediaRate>;
  getMediaRates: ListApiCall<MediaRate>;
  getMediaRateFieldOptions: ListFieldsOptionsApiCall;
  getUserSelf: () => ApiPromise<User>;
  getUsers: ListApiCall<User>;
  getUserFields: ListFieldsApiCall<User>;
  getUserFieldOptions: ListFieldsOptionsApiCall;
  getUser: GetApiCall<User>;
  updateUser: PutApiCall<User>;
  uploadFile: (file: File) => ApiPromise<{ url: string }>;
  createSponsorshipFieldOption: CreateOptionApiCall;
  createMediaRateFieldOption: CreateOptionApiCall;
}

export type UploadedFile = string;

export interface PaginatedResponse<T> extends PaginationRes {
  results: T[];
}

export interface PaginationBase {
  page: number;
  page_size: number;
}

export interface PaginationRes extends PaginationBase {
  count: number;
}

export type Flat<T extends Entity> = {
  [key in keyof T]: string | number | boolean | null;
};

export type PostPayload<T extends Entity> = Omit<Flat<T>, "id">;

export const isSelectFieldOption = <T extends Entity>(
  v: EntityValueType
): v is ISelectFieldOption => {
  if (!v) {
    return false;
  }
  return (v as ISelectFieldOption).label !== undefined;
};

export const isListValue = <T extends Entity>(
  v: EntityValue
): v is EntityValueType[] => {
  return Array.isArray(v);
};

export const flattenSingleValue = <T extends Entity>(
  v: EntityValueType
): EntityValueFlatType => {
  if (v === undefined) {
    return v;
  }
  if (isSelectFieldOption(v)) {
    return v.value;
  }
  if (typeof v === "number") {
    return v.toString();
  }
  return v;
};

export const flattenValue = <T extends Entity>(
  v: EntityValue
): EntityValueFlat => {
  if (isListValue(v)) {
    return v.map(flattenSingleValue);
  }
  return flattenSingleValue(v);
};

export const flatten = <T extends Entity>(obj: T): Flat<T> => {
  return Object.entries(obj).reduce(
    (prev, [key, value]) => ({
      ...prev,
      [key]: flattenValue(value),
    }),
    {} as Flat<T>
  );
};
